/**
 * ModuleFieldsTab — F12.D T12.D.9 (+ szlif UX 2026-06-15: pola modułów systemowych).
 *
 * Globalny widok wszystkich pól modułów w Ustawieniach tenanta — systemowych i własnych.
 * Rozdział przez filtr zakresu (segmented): Wszystkie / Systemowe / Własne.
 *
 * Moduły systemowe (Kontrahenci, Pracownicy, Firmy, Użytkownicy…):
 *  - pola pokazywane do ODCZYTU; pól systemowych NIE można usunąć z tego widoku,
 *  - edycja odbywa się w „Ustawieniach modułu" (`/system-modules/:slug/settings?tab=fields`).
 *
 * Moduły własne:
 *  - pełne zarządzanie jak dotąd — „+ Dodaj pole", usuwanie (poza polami z szablonu kreatora),
 *    edycja w kreatorze.
 *
 * „+ Dodaj pole" dotyczy WYŁĄCZNIE modułów własnych — pole dodatkowe do modułu systemowego
 * dodaje się w jego ustawieniach.
 *
 * Backend:
 *  - GET `/api/modules` + `/api/modules/:id` (moduły własne + pola)
 *  - GET `/api/system-modules` + `/api/system-modules/:slug/fields` (moduły systemowe + pola)
 *    (fetch per moduł równolegle — brak aggregate endpointu, optymalizacja na później jeśli trzeba).
 *
 * NEG (deferred):
 *  - Edycja pola inline → link do kreatora (własne) / ustawień modułu (systemowe).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { DefaultValueUnavailableReason, FieldValueDomain } from '@saas/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Check, Eye, EyeOff, Loader2, Lock, Pencil, Plus, Search, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { CURATED_FIELD_TYPES, fieldTypeI18nKey } from '@/components/fields/curated-types';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { renderIconByName } from '@/components/modules/IconPicker';
import { Spinner } from '@/components/Spinner';
import {
  SYSTEM_FIELD_TYPES,
  type SystemFieldType,
  systemFieldTypeI18nKey,
} from '@/components/system-modules/AddSystemFieldModal';
import {
  DefaultValueCell,
  type DefaultValueCellField,
} from '@/components/system-modules/DefaultValueCell';
import {
  type FieldDefaultSaver,
  useFieldDefaultSaver,
} from '@/components/system-modules/useFieldDefaultSaver';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { AddFieldModal } from './AddFieldModal';

// ─── Typy ─────────────────────────────────────────────────────────────────────

type FieldScope = 'all' | 'system' | 'custom';

interface CustomModuleListItem {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
}

interface SystemModuleListItem {
  slug: string;
  name: string;
  entityTableName: string;
  icon: string;
  color: string;
}

interface CustomModuleField {
  id: string;
  moduleId: string;
  name: string;
  slug: string;
  fieldType: string;
  description: string | null;
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  showInList: boolean;
  /** Pole z szablonu kreatora (Krok 6) — chronione: klient nie usuwa ani nie przemianowuje. */
  sourceTemplateModuleId: string | null;
}

interface CustomModuleDetailResponse {
  module: CustomModuleListItem & { fields: CustomModuleField[] };
}

/** Wiersz `system_module_fields` z `GET /system-modules/:slug/fields`. */
interface SystemModuleFieldRow {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
  description: string | null;
  isCore: boolean;
  isSystemLocked: boolean;
  isRequired: boolean;
  isSearchable: boolean;
  showInList: boolean;
  sortOrder: number;
  /** Wartość domyślna formularza nowego rekordu + dziedzina pola (lista pól, `withFieldDefaultability`). */
  defaultValue: string | null;
  defaultable: boolean;
  valueDomain: FieldValueDomain | null;
  notDefaultableReason: DefaultValueUnavailableReason | null;
}

/** Pole znormalizowane do wspólnego renderu (systemowe i własne). */
interface UnifiedField {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
  description: string | null;
  isRequired: boolean;
  isSearchable: boolean;
  showInList: boolean;
  /**
   * Rozdział PO POLU (nie po module): `system` = pole wbudowane/zablokowane
   * (`is_core`/`is_system_locked`), `custom` = pole dodatkowe (ext na module systemowym
   * LUB dowolne pole modułu własnego). Pole dodatkowe na module systemowym to też „własne".
   */
  fieldKind: 'system' | 'custom';
  /** custom — pole z szablonu kreatora (chronione przed usunięciem). */
  sourceTemplateModuleId?: string | null;
  /**
   * Wartość domyślna — tylko pola modułów SYSTEMOWYCH (wbudowane i dodatkowe). Moduły własne
   * mają ją w kreatorze modułu (bez zmian w etapie 1).
   */
  defaultValueField?: DefaultValueCellField & { defaultValue: string | null };
}

interface UnifiedGroupMeta {
  kind: 'system' | 'custom';
  /** tylko custom */
  id?: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
}

interface UnifiedGroup {
  meta: UnifiedGroupMeta;
  fields: UnifiedField[];
}

// ─── Helpery etykiet typów (curated dla własnych + system dla systemowych) ──────

const CURATED_TYPE_SET = new Set<string>(CURATED_FIELD_TYPES);
const SYSTEM_TYPE_SET = new Set<string>(SYSTEM_FIELD_TYPES);

/** Zwraca klucz i18n dla typu pola; raw `type` gdy nieznany (t() z defaultValue zwróci samo type). */
function fieldTypeLabelKey(type: string): string {
  if (CURATED_TYPE_SET.has(type)) return fieldTypeI18nKey(type);
  if (SYSTEM_TYPE_SET.has(type)) return systemFieldTypeI18nKey(type as SystemFieldType);
  return type;
}

const SCOPES: FieldScope[] = ['all', 'system', 'custom'];

// ─── Komponent ──────────────────────────────────────────────────────────────────

export function ModuleFieldsTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [scope, setScope] = useState<FieldScope>('all');
  /** Klucz wybranego modułu: `c:<id>` (własny) lub `s:<slug>` (systemowy); '' = wszystkie. */
  const [filterModuleKey, setFilterModuleKey] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState<{
    moduleId: string;
    fieldId: string;
    fieldName: string;
  } | null>(null);

  // 1) listy modułów
  const customModulesQuery = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: CustomModuleListItem[] }>('/modules');
      return res.data.modules;
    },
  });

  const systemModulesQuery = useQuery({
    queryKey: ['system-modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: SystemModuleListItem[] }>('/system-modules');
      return res.data.modules;
    },
    // 403 gdy brak uprawnień do modułów systemowych — degradujemy cicho (zero retry).
    retry: false,
  });

  // Mutation do usuwania pola — TYLKO moduły własne
  const deleteMutation = useMutation({
    mutationFn: async ({ moduleId, fieldId }: { moduleId: string; fieldId: string }) => {
      await api.delete(`/modules/${moduleId}/fields/${fieldId}`);
    },
    onSuccess: async (_data, variables) => {
      toast.show({ variant: 'success', title: t('tenant.moduleFields.toasts.deleted') });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['module-fields'] }),
        queryClient.refetchQueries({ queryKey: ['module-fields', variables.moduleId] }),
        queryClient.refetchQueries({ queryKey: ['modules'] }),
        queryClient.refetchQueries({ queryKey: ['module-records'] }),
      ]);
    },
    onError: (err) => {
      // Krok 6: pole z szablonu jest chronione po stronie backendu (403) — czytelny komunikat
      // zamiast surowego kodu. Przycisk usuwania i tak jest wyszarzony dla takich pól (poniżej),
      // ten toast to siatka bezpieczeństwa (np. wyścig: pole oznaczone jako szablonowe między fetchami).
      const code = isAxiosError(err) ? err.response?.data?.error : undefined;
      toast.show({
        variant: 'error',
        title: t('tenant.moduleFields.toasts.deleteError'),
        description:
          code === 'TEMPLATE_FIELD_PROTECTED'
            ? t('tenant.moduleFields.toasts.templateProtected')
            : undefined,
      });
    },
  });

  // Przełącznik widoczności na liście dla pól modułów SYSTEMOWYCH. Backend pozwala zmieniać
  // `showInList` także dla pól wbudowanych (zablokowane są tylko slug/typ/flagi rdzenia), więc
  // klient steruje widocznością wprost z tego widoku — bez wchodzenia w ustawienia modułu.
  const visibilityMutation = useMutation({
    mutationFn: async ({
      slug,
      fieldId,
      showInList,
    }: {
      slug: string;
      fieldId: string;
      showInList: boolean;
    }) => {
      await api.put(`/system-modules/${slug}/fields/${fieldId}`, { showInList });
    },
    onSuccess: async (_data, variables) => {
      toast.show({ variant: 'success', title: t('tenant.moduleFields.toasts.visibilityUpdated') });
      await queryClient.refetchQueries({
        queryKey: ['system-module-fields-list', variables.slug],
      });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.moduleFields.toasts.visibilityError') });
    },
  });

  // Wartość domyślna pól modułów systemowych — ten sam zapis co w Ustawienia kartoteki → Pola.
  const defaultSaver = useFieldDefaultSaver();

  const handleToggleVisibility = (slug: string, fieldId: string, nextShowInList: boolean) => {
    visibilityMutation.mutate({ slug, fieldId, showInList: nextShowInList });
  };

  const handleDeleteField = (moduleId: string, fieldId: string, fieldName: string) => {
    setPendingDelete({ moduleId, fieldId, fieldName });
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(
      { moduleId: pendingDelete.moduleId, fieldId: pendingDelete.fieldId },
      { onSettled: () => setPendingDelete(null) },
    );
  };

  const handleScopeChange = (next: FieldScope) => {
    setScope(next);
    // Słownik typów różni się per zakres (własne vs systemowe) — reset, by nie zostać
    // z typem nieobecnym w nowym zakresie. Wybór modułu zostaje (dropdown listuje wszystkie).
    setFilterType('');
  };

  const customModules = useMemo(() => customModulesQuery.data ?? [], [customModulesQuery.data]);
  const systemModules = useMemo(() => systemModulesQuery.data ?? [], [systemModulesQuery.data]);

  // 2) per moduł — fetch pól równolegle
  const customDetailsQueries = useQueries({
    queries: customModules.map((m) => ({
      queryKey: ['module-fields', m.id],
      queryFn: async () => {
        const res = await api.get<CustomModuleDetailResponse>(`/modules/${m.id}`);
        return res.data.module;
      },
      enabled: !!m.id,
    })),
  });

  const systemFieldsQueries = useQueries({
    queries: systemModules.map((m) => ({
      queryKey: ['system-module-fields-list', m.slug],
      queryFn: async () => {
        const res = await api.get<{ fields: SystemModuleFieldRow[] }>(
          `/system-modules/${m.slug}/fields`,
        );
        return res.data.fields;
      },
      enabled: !!m.slug,
    })),
  });

  const isLoading =
    customModulesQuery.isLoading ||
    systemModulesQuery.isLoading ||
    customDetailsQueries.some((q) => q.isLoading) ||
    systemFieldsQueries.some((q) => q.isLoading);

  // Błąd modułów WŁASNYCH jest twardy (to pierwotny widok). Błąd systemowych (np. 403 brak
  // uprawnień) degradujemy cicho do pustej listy systemowej — widok własnych dalej działa.
  const isError = customModulesQuery.isError || customDetailsQueries.some((q) => q.isError);

  // 3) normalizacja do wspólnych grup — systemowe najpierw (spójnie z przeglądem modułów),
  //    potem własne.
  const allGroups = useMemo<UnifiedGroup[]>(() => {
    const groups: UnifiedGroup[] = [];

    systemModules.forEach((m, i) => {
      const rows = systemFieldsQueries[i]?.data ?? [];
      groups.push({
        meta: { kind: 'system', slug: m.slug, name: m.name, icon: m.icon, color: m.color },
        fields: rows.map((r) => ({
          id: r.id,
          name: r.name,
          slug: r.slug,
          fieldType: r.fieldType,
          description: r.description,
          isRequired: r.isRequired,
          isSearchable: r.isSearchable,
          showInList: r.showInList,
          // Pole wbudowane/zablokowane = systemowe; ext (dodane przez klienta) = własne.
          fieldKind: r.isCore || r.isSystemLocked ? 'system' : 'custom',
          defaultValueField: {
            slug: r.slug,
            fieldType: r.fieldType,
            defaultValue: r.defaultValue,
            defaultable: r.defaultable,
            valueDomain: r.valueDomain,
            notDefaultableReason: r.notDefaultableReason,
          },
        })),
      });
    });

    customModules.forEach((m, i) => {
      const detail = customDetailsQueries[i]?.data;
      groups.push({
        meta: {
          kind: 'custom',
          id: m.id,
          slug: m.slug,
          name: m.name,
          icon: m.icon,
          color: m.color,
        },
        fields: (detail?.fields ?? []).map((f) => ({
          id: f.id,
          name: f.name,
          slug: f.slug,
          fieldType: f.fieldType,
          description: f.description,
          isRequired: f.isRequired,
          isSearchable: f.isSearchable,
          showInList: f.showInList,
          // Wszystkie pola modułu własnego są „własne".
          fieldKind: 'custom',
          sourceTemplateModuleId: f.sourceTemplateModuleId,
        })),
      });
    });

    return groups;
  }, [systemModules, customModules, systemFieldsQueries, customDetailsQueries]);

  // 4) zakres (PO POLU) + wybór modułu. Rozdział systemowe/własne jest na poziomie pola:
  //    moduł systemowy z polem dodatkowym pojawi się i pod „Systemowe" (pola wbudowane),
  //    i pod „Własne" (pola dodatkowe) — „w obu miejscach". Decyduje też o dostępnych typach.
  const scopeGroups = useMemo(() => {
    return allGroups
      .filter((g) => {
        if (!filterModuleKey) return true;
        const key = g.meta.kind === 'system' ? `s:${g.meta.slug}` : `c:${g.meta.id}`;
        return key === filterModuleKey;
      })
      .map((g) => ({
        meta: g.meta,
        fields: g.fields.filter((f) => scope === 'all' || f.fieldKind === scope),
      }))
      .filter((g) => g.fields.length > 0);
  }, [allGroups, scope, filterModuleKey]);

  // 5) typy dostępne w aktualnym zakresie (dynamiczny dropdown — słownik typów różni się
  //    między polami własnymi a systemowymi)
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    scopeGroups.forEach((g) => g.fields.forEach((f) => set.add(f.fieldType)));
    return [...set].sort((a, b) =>
      t(fieldTypeLabelKey(a), { defaultValue: a }).localeCompare(
        t(fieldTypeLabelKey(b), { defaultValue: b }),
        'pl',
      ),
    );
  }, [scopeGroups, t]);

  // 6) filtr typu + szukajka na poziomie pól
  const groupedFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopeGroups.map((g) => ({
      meta: g.meta,
      fields: g.fields.filter((f) => {
        if (filterType && f.fieldType !== filterType) return false;
        if (q && !f.name.toLowerCase().includes(q) && !f.slug.toLowerCase().includes(q)) {
          return false;
        }
        return true;
      }),
    }));
  }, [scopeGroups, filterType, search]);

  const totalFields = groupedFields.reduce((sum, g) => sum + g.fields.length, 0);
  const totalModules = groupedFields.filter((g) => g.fields.length > 0).length;

  const defaultAddModuleId = filterModuleKey.startsWith('c:')
    ? filterModuleKey.slice(2)
    : undefined;

  return (
    <div className="space-y-4" data-testid="module-fields-tab">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-text-primary">{t('tenant.moduleFields.title')}</h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.moduleFields.subtitle')}</p>
          {!isLoading && !isError && (
            <p className="mt-2 text-xs text-text-muted">
              {t('tenant.moduleFields.summary', {
                fields: totalFields,
                modules: totalModules,
              })}
            </p>
          )}
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
          data-testid="module-fields-add-btn"
          disabled={customModules.length === 0}
        >
          {t('tenant.moduleFields.addField')}
        </Button>
      </header>

      {/* Filtry */}
      <Card padding="md">
        <div className="space-y-3">
          {/* Zakres (segmented) */}
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              {t('tenant.moduleFields.scopeLabel')}
            </label>
            <div
              role="tablist"
              aria-label={t('tenant.moduleFields.scopeLabel')}
              className="inline-flex rounded-lg border border-border-primary bg-bg-secondary p-0.5"
              data-testid="module-fields-scope"
            >
              {SCOPES.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={scope === s}
                  onClick={() => handleScopeChange(s)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    scope === s
                      ? 'bg-bg-primary text-text-primary shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid={`module-fields-scope-${s}`}
                >
                  {t(`tenant.moduleFields.scope.${s}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Moduł / typ / szukajka */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-text-secondary">
                {t('tenant.moduleFields.filterModule')}
              </label>
              <Dropdown
                value={filterModuleKey}
                onChange={(v) => setFilterModuleKey(v)}
                data-testid="module-fields-filter-module"
                aria-label={t('tenant.moduleFields.filterModule')}
                options={[{ value: '', label: t('tenant.moduleFields.filterModuleAll') }]}
                groups={[
                  ...(systemModules.length > 0
                    ? [
                        {
                          label: t('tenant.moduleFields.scope.system'),
                          options: systemModules.map((m) => ({
                            value: `s:${m.slug}`,
                            label: m.name,
                          })),
                        },
                      ]
                    : []),
                  ...(customModules.length > 0
                    ? [
                        {
                          label: t('tenant.moduleFields.scope.custom'),
                          options: customModules.map((m) => ({
                            value: `c:${m.id}`,
                            label: m.name,
                          })),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-text-secondary">
                {t('tenant.moduleFields.filterType')}
              </label>
              <Dropdown
                value={filterType}
                onChange={setFilterType}
                options={[
                  { value: '', label: t('tenant.moduleFields.filterTypeAll') },
                  ...availableTypes.map((type) => ({
                    value: type,
                    label: t(fieldTypeLabelKey(type), { defaultValue: type }),
                  })),
                ]}
                aria-label={t('tenant.moduleFields.filterType')}
                data-testid="module-fields-filter-type"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-text-secondary">&nbsp;</label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                  aria-hidden="true"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('tenant.moduleFields.searchPlaceholder')}
                  className="pl-9"
                  data-testid="module-fields-search"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Lista pól */}
      {isLoading && (
        <div className="flex justify-center py-12" data-testid="module-fields-loading">
          <Spinner />
        </div>
      )}

      {isError && (
        <Card padding="md">
          <p className="text-error-500">{t('tenant.moduleFields.loadError')}</p>
        </Card>
      )}

      {!isLoading && !isError && totalFields === 0 && (
        <Card padding="md" data-testid="module-fields-empty">
          <p className="text-sm text-text-tertiary">{t('tenant.moduleFields.empty')}</p>
        </Card>
      )}

      {!isLoading && !isError && totalFields > 0 && (
        <div className="space-y-4">
          {groupedFields.map((group) => {
            if (group.fields.length === 0) return null;
            return (
              <ModuleFieldsGroup
                key={`${group.meta.kind}:${group.meta.slug}`}
                meta={group.meta}
                fields={group.fields}
                onDeleteField={handleDeleteField}
                isDeleting={deleteMutation.isPending}
                onToggleVisibility={handleToggleVisibility}
                togglingFieldId={
                  visibilityMutation.isPending
                    ? (visibilityMutation.variables?.fieldId ?? null)
                    : null
                }
                defaultSaver={defaultSaver}
              />
            );
          })}
        </div>
      )}

      <AddFieldModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        modules={customModules.map((m) => ({ id: m.id, name: m.name }))}
        defaultModuleId={defaultAddModuleId}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title={t('tenant.moduleFields.actions.delete')}
        message={
          pendingDelete
            ? t('tenant.moduleFields.confirmDelete', { name: pendingDelete.fieldName })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ─── Grupa pól per moduł ──────────────────────────────────────────────────────

interface ModuleFieldsGroupProps {
  meta: UnifiedGroupMeta;
  fields: UnifiedField[];
  onDeleteField: (moduleId: string, fieldId: string, fieldName: string) => void;
  isDeleting: boolean;
  /** Przełącza widoczność na liście (showInList) — używane tylko dla modułów systemowych. */
  onToggleVisibility: (slug: string, fieldId: string, nextShowInList: boolean) => void;
  /** Id pola, którego widoczność jest aktualnie zapisywana (spinner/disable). */
  togglingFieldId: string | null;
  /** Zapis wartości domyślnej pól modułów systemowych. */
  defaultSaver: FieldDefaultSaver;
}

function fieldCountLabel(n: number): string {
  return n === 1 ? 'pole' : n < 5 && n > 1 ? 'pola' : 'pól';
}

function ModuleFieldsGroup({
  meta,
  fields,
  onDeleteField,
  isDeleting,
  onToggleVisibility,
  togglingFieldId,
  defaultSaver,
}: ModuleFieldsGroupProps) {
  const { t } = useTranslation();
  const isSystem = meta.kind === 'system';
  const settingsLink = isSystem
    ? `/system-modules/${meta.slug}/settings?tab=fields`
    : `/modules/${meta.id}/builder`;
  const editLinkLabel = isSystem
    ? t('tenant.systemModules.actions.openSettings')
    : t('tenant.modules.actions.editBuilder');

  return (
    <Card
      padding="none"
      className="overflow-hidden"
      data-testid={`module-fields-group-${meta.kind}-${meta.slug}`}
    >
      <header className="flex items-center justify-between border-b border-border-primary bg-bg-secondary px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${meta.color}20`, color: meta.color }}
            aria-hidden="true"
          >
            {renderIconByName(meta.icon, 'h-5 w-5')}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-text-primary">{meta.name}</span>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                  isSystem
                    ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300'
                    : 'bg-bg-tertiary text-text-muted'
                }`}
              >
                {t(`tenant.moduleFields.groupBadge.${meta.kind}`)}
              </span>
            </div>
            <div className="text-xs text-text-tertiary">
              /{meta.slug} — {fields.length} {fieldCountLabel(fields.length)}
            </div>
          </div>
        </div>
        <Link
          to={settingsLink}
          className="text-xs text-accent-600 hover:text-accent-700 hover:underline"
        >
          {editLinkLabel} →
        </Link>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-b border-border-primary bg-bg-secondary text-left text-xs font-medium uppercase tracking-wider text-text-muted">
              <th className="px-3 py-2">{t('tenant.moduleFields.columns.name')}</th>
              <th className="px-3 py-2">{t('tenant.moduleFields.columns.type')}</th>
              <th className="px-3 py-2">{t('tenant.moduleFields.columns.slug')}</th>
              <th className="px-3 py-2 text-center">{t('tenant.moduleFields.columns.required')}</th>
              <th className="px-3 py-2 text-center">
                {t('tenant.moduleFields.columns.showInList')}
              </th>
              <th className="px-3 py-2 text-center">
                {t('tenant.moduleFields.columns.searchable')}
              </th>
              <th className="px-3 py-2">{t('tenant.systemModuleFields.defaultValue.label')}</th>
              {/* Akcje tylko dla modułów własnych — pola modułów systemowych zarządza się
                  z poziomu samego modułu (link „Otwórz ustawienia" w nagłówku grupy). */}
              {!isSystem && (
                <th className="px-3 py-2 w-20 text-right">
                  {t('tenant.moduleFields.columns.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => (
              <tr
                key={f.id}
                className="border-b border-border-primary last:border-b-0 hover:bg-bg-tertiary/50"
                data-testid={`module-field-row-${f.slug}`}
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">{f.name}</span>
                    {/* W grupach modułów systemowych rozróżniamy pole wbudowane vs dodatkowe. */}
                    {isSystem &&
                      (f.fieldKind === 'system' ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted"
                          title={t('tenant.systemModuleFields.lockedTooltip')}
                          data-testid={`module-field-row-${f.slug}-kind-system`}
                        >
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          {t('tenant.moduleFields.scope.system')}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center rounded-full bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent-700 dark:bg-accent-900/30 dark:text-accent-300"
                          data-testid={`module-field-row-${f.slug}-kind-custom`}
                        >
                          {t('tenant.moduleFields.scope.custom')}
                        </span>
                      ))}
                  </div>
                  {f.description && (
                    <div className="text-xs text-text-tertiary line-clamp-1">{f.description}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-text-secondary">
                  {t(fieldTypeLabelKey(f.fieldType), { defaultValue: f.fieldType })}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-text-tertiary">{f.slug}</td>
                <td className="px-3 py-2 text-center">
                  {f.isRequired ? (
                    <Check className="mx-auto h-4 w-4 text-accent-600" aria-label="required" />
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {isSystem ? (
                    // Pola systemowe: widoczność na liście jest EDYTOWALNA wprost tutaj
                    // (nazwa/typ/usuwanie nadal zablokowane). PUT showInList — backend dopuszcza
                    // to także dla pól wbudowanych.
                    <IconButton
                      size="sm"
                      onClick={() => onToggleVisibility(meta.slug, f.id, !f.showInList)}
                      disabled={togglingFieldId === f.id}
                      className="mx-auto"
                      aria-pressed={f.showInList}
                      aria-label={
                        f.showInList
                          ? t('tenant.moduleFields.actions.hideFromList')
                          : t('tenant.moduleFields.actions.showInList')
                      }
                      title={
                        f.showInList
                          ? t('tenant.moduleFields.actions.hideFromList')
                          : t('tenant.moduleFields.actions.showInList')
                      }
                      data-testid={`module-field-row-${f.slug}-visibility`}
                    >
                      {togglingFieldId === f.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                      ) : f.showInList ? (
                        <Eye className="h-4 w-4 text-accent-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-text-muted" />
                      )}
                    </IconButton>
                  ) : f.showInList ? (
                    <Eye className="mx-auto h-4 w-4 text-accent-600" aria-label="in list" />
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {f.isSearchable ? (
                    <Search className="mx-auto h-4 w-4 text-accent-600" aria-label="searchable" />
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
                <td className="w-64 px-3 py-2" data-testid={`module-field-row-${f.slug}-default`}>
                  {f.defaultValueField ? (
                    <DefaultValueCell
                      moduleSlug={meta.slug}
                      field={f.defaultValueField}
                      value={defaultSaver.valueOf(f.id, f.defaultValueField.defaultValue)}
                      onChange={(next) => defaultSaver.save(meta.slug, f.id, next)}
                    />
                  ) : (
                    // Moduły własne: wartość domyślna w kreatorze modułu (etap 1 bez zmian).
                    <span className="text-xs text-text-muted">
                      {t('tenant.moduleFields.defaultValueInBuilder')}
                    </span>
                  )}
                </td>
                {/* Edycja/usuwanie tylko dla modułów własnych. */}
                {!isSystem && (
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={settingsLink}
                        className="inline-flex items-center justify-center rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
                        aria-label={editLinkLabel}
                        title={editLinkLabel}
                        data-testid={`module-field-row-${f.slug}-edit`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Link>
                      <IconButton
                        size="sm"
                        onClick={() => onDeleteField(meta.id!, f.id, f.name)}
                        disabled={isDeleting || !!f.sourceTemplateModuleId}
                        className="text-text-muted hover:bg-red-50 hover:text-red-600 disabled:hover:bg-transparent disabled:hover:text-text-muted dark:hover:bg-red-900/20"
                        aria-label={t('tenant.moduleFields.actions.delete')}
                        title={
                          f.sourceTemplateModuleId
                            ? t('tenant.moduleFields.actions.templateProtectedTooltip')
                            : t('tenant.moduleFields.actions.delete')
                        }
                        data-testid={`module-field-row-${f.slug}-delete`}
                      >
                        <Trash2 />
                      </IconButton>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
